import { IEventDataSource, IHttpClient, IHttpResponse } from "@/types/interfaces";
import { BigBashEvent, Customer, CustomerSchema, EventSchema, Game, GameOrderSnapshot, GameOrderSnapshotSchema, GamePoint, GamePointSchema, GameSchema, RoundPointsFieldEnum } from "@/types/types";
import EventConflictError from "@/utils/EventConflictError";
import FetchHttpClient from "@/utils/FetchHttpClient";

const HTTP_STATUS_CONFLICT = 409;
const UNREADABLE_RESPONSE_RETRIES = 1;

class ApiManager implements IEventDataSource {
	private readonly baseUrl: string;
	private readonly httpClient: IHttpClient;

	constructor(baseUrl = "/api", httpClient: IHttpClient = new FetchHttpClient()) {
		this.baseUrl = baseUrl;
		this.httpClient = httpClient;
	}

	async getEventById(eventId: string): Promise<BigBashEvent> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-event/${eventId}/`);
		await this._throwErrorOnBadResponse(response);
		const eventJson = await response.json();
		const eventData = eventJson.data;
		return EventSchema.parse(eventData);
	}

	async getAllEvents(): Promise<BigBashEvent[]> {
		const eventsJson = await this._fetchAllEvents();
		return this._parseEventsJSON(eventsJson);
	}

	private async _fetchAllEvents(): Promise<any[]> {
		let lastUnreadableResponseError: unknown;
		for (let attempt = 0; attempt <= UNREADABLE_RESPONSE_RETRIES; attempt++) {
			const response = await this.httpClient.get(`${this.baseUrl}/get-all-events/`);
			await this._throwErrorOnBadResponse(response);
			try {
				const responseObject = await response.json();
				return responseObject.data || [];
			} catch (error) {
				lastUnreadableResponseError = error;
			}
		}
		throw new Error('Could not read the events response from the backend.', { cause: lastUnreadableResponseError });
	}

	private _parseEventsJSON(jsonData: any[]): BigBashEvent[] {
		return EventSchema.array().parse(jsonData);
	}

	async createEvent(event: BigBashEvent): Promise<BigBashEvent> {
		const response = await this.httpClient.post(`${this.baseUrl}/create-event/`, event);

		await this._throwErrorOnBadResponse(response);

		try {
			const responseData = await response.json();
			if (!responseData.success) {
				throw new Error(`Server error: ${responseData.message || 'Unknown error'}`);
			}
			return EventSchema.parse(responseData.data);
		} catch (error) {
			if (error instanceof Error) {
				throw error;
			}
			throw new Error('Invalid JSON response when creating event');
		}
	}

	async updateEvent(event: BigBashEvent): Promise<BigBashEvent> {
		const response = await this.httpClient.put(`${this.baseUrl}/update-event/${event.id}/`, event);
		if (response.status === HTTP_STATUS_CONFLICT) {
			const errorData = await response.json();
			throw new EventConflictError(errorData.message || 'Event was changed by another device');
		}
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return EventSchema.parse(responseData.data);
	}

	async deleteEvent(eventId: string): Promise<void> {
		const response = await this.httpClient.delete(`${this.baseUrl}/delete-event/${eventId}/`);
		await this._throwErrorOnBadResponse(response);
	}

	async deleteGamePointsForGame(eventId: string, gameId: string): Promise<void> {
		const response = await this.httpClient.delete(`${this.baseUrl}/delete-game-points/${eventId}/${gameId}/`);
		await this._throwErrorOnBadResponse(response);
	}

	async getGamePoints(eventId: string, gameId: string): Promise<GamePoint[]> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-game-points/${eventId}/${gameId}/`);
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return GamePointSchema.array().parse(responseData.data);
	}

	async setRoundPointValue(eventId: string, gameId: string, teamId: string, roundNumber: number, field: RoundPointsFieldEnum, value: number): Promise<GamePoint> {
		const response = await this.httpClient.post(`${this.baseUrl}/set-round-points/`, {
			event: eventId,
			game: gameId,
			team: teamId,
			round: roundNumber,
			field,
			value,
		});
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return GamePointSchema.parse(responseData.data);
	}

	async setRoundTargetValue(eventId: string, gameId: string, roundNumber: number, value: number): Promise<GamePoint[]> {
		const response = await this.httpClient.post(`${this.baseUrl}/set-round-target-value/`, {
			event: eventId,
			game: gameId,
			round: roundNumber,
			value,
		});
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return GamePointSchema.array().parse(responseData.data);
	}

	async swapGame(eventId: string, previousGameId: string, newGameId: string): Promise<BigBashEvent> {
		const response = await this.httpClient.post(`${this.baseUrl}/swap-game/`, {
			event: eventId,
			previous_game: previousGameId,
			new_game: newGameId,
		});
		if (response.status === HTTP_STATUS_CONFLICT) {
			const errorData = await response.json();
			throw new EventConflictError(errorData.message || 'Game was swapped by another device');
		}
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return EventSchema.parse(responseData.data);
	}

	async getGameOrder(eventId: string): Promise<GameOrderSnapshot> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-game-order/${eventId}/`);
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return GameOrderSnapshotSchema.parse(responseData.data);
	}

	async getDefaultGames(): Promise<Game[]> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-default-games/`);
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return responseData.data.map((game: any) => GameSchema.parse(game));
	}

	async getDefaultCustomer(): Promise<Customer> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-default-customer/`);
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return CustomerSchema.parse(responseData.data);
	}

	async getAllGames(): Promise<Game[]> {
		const response = await this.httpClient.get(`${this.baseUrl}/get-all-games/`);
		await this._throwErrorOnBadResponse(response);
		const responseData = await response.json();
		return responseData.data.map((game: any) => GameSchema.parse(game));
	}

	private async _throwErrorOnBadResponse(response: IHttpResponse) {
		if (!response.ok) {
			const errorData = await response.json();
			const backendMessage = errorData.message || 'Unknown error';
			throw new Error(`${backendMessage}`);
		}
	}
}

export default ApiManager;